/**
 * Drainage Coefficient Service
 * Drainage product properties based on Maccaferri product specifications
 * Extended with DATABASE.txt parameters for complete MacDrain calculations
 *
 * Data source: DrainageDataService (API-fetched from backend)
 */

import { DrainageDataService } from './DrainageDataService';
import { GEOGRID_PRODUCTS } from '../constants/AASHTOConstants';

interface DrainageProductProperties {
    effectivePorosity: number; // No (effective porosity)
    permeabilityCoefficient: number; // k (permeability coefficient in m/day)
    description: string;
    // DATABASE.txt specific parameters for MacDrain products
    transmissivity?: number; // R/S i=0.03 (DATABASE E column)
    thickness?: number; // Thickness in meters (DATABASE F column)
    rfcreep?: number; // RFcreep - 100kPa - 100 years factor (DATABASE G column)
}

/**
 * Get drainage products (lazy-loaded from DrainageDataService)
 */
const getDrainageProducts = (): { [key: string]: DrainageProductProperties } => {
    return DrainageDataService.getProducts() as unknown as { [key: string]: DrainageProductProperties };
};

export class DrainageCoefficientService {
    /**
     * Determine if a product is a geogrid (reinforcement-only, no significant drainage enhancement)
     */
    static isGeogridProduct(productId?: string): boolean {
        if (!productId) return true; // No product = geogrid-only behavior

        // MacGrid products and geogrid_only are geogrids (minimal drainage enhancement)
        return productId === GEOGRID_PRODUCTS.GEOGRID_ONLY || productId.startsWith('macgrid_eg_');
    }

    /**
     * Determine if a product has significant drainage capabilities
     */
    static isDrainageProduct(productId?: string): boolean {
        if (!productId) return false;

        // All MacDrain products, Multicomposito, and GCD have significant drainage capabilities
        return productId.startsWith('macdrain_w_') || productId === GEOGRID_PRODUCTS.MULTICOMPOSITO || productId === GEOGRID_PRODUCTS.GEOCOMPOSITE_DRAINAGE;
    }

    /**
     * Get drainage product properties by product ID
     */
    static getDrainageProductProperties(productId?: string): DrainageProductProperties {
        const products = getDrainageProducts();
        if (!productId || !products[productId]) {
            // Return default values if product not found (from centralized configuration)
            return products[DrainageDataService.getDefaultProduct()];
        }
        return products[productId];
    }

    /**
     * Calculate drainage time using DATABASE.txt transmissivity formula
     * Formula: carriageWayWidth / ((transmissivity/1000) / thickness)
     * From DATABASE.txt H42: ='1_Road_Info'!$D$3/((E42/1000)/F42)
     *
     * @param carriagewayWidth - Width of carriageway in meters (from '1_Road_Info'!D3)
     * @param productId - MacDrain product ID
     * @param useShortTerm - Whether to use short-term (true) or long-term (false) calculation
     * @returns Drainage time calculation results
     */
    static calculateTransmissivityDrainageTime(
        carriagewayWidth: number,
        productId: string,
        useShortTerm = true
    ): {
        drainageTimeSeconds: number;
        drainageTimeHours: number;
        drainageTimeDays: number;
        drainageTimeWeeks: number;
        drainageTimeMonths: number;
        formula: string;
        productInfo: DrainageProductProperties;
    } {
        const productInfo = this.getDrainageProductProperties(productId);

        if (!productInfo.transmissivity || !productInfo.thickness) {
            throw new Error(`Product ${productId} does not have transmissivity data required for DATABASE.txt calculations`);
        }

        // DATABASE.txt formulas:
        // Short-term (H column): carriageWayWidth / ((transmissivity/1000) / thickness)
        // Long-term (M column): carriageWayWidth / (((transmissivity/1000) / rfcreep) / thickness)

        let drainageTimeSeconds: number;
        let formula: string;

        if (useShortTerm) {
            // Short-term formula (DATABASE H42-H48)
            drainageTimeSeconds = carriagewayWidth / (productInfo.transmissivity / 1000 / productInfo.thickness);
            formula = `${carriagewayWidth} / ((${productInfo.transmissivity}/1000) / ${productInfo.thickness})`;
        } else {
            // Long-term formula with RFcreep (DATABASE M42-M48)
            if (!productInfo.rfcreep) {
                throw new Error(`Product ${productId} does not have RFcreep data required for long-term calculations`);
            }
            drainageTimeSeconds = carriagewayWidth / (productInfo.transmissivity / 1000 / productInfo.rfcreep / productInfo.thickness);
            formula = `${carriagewayWidth} / (((${productInfo.transmissivity}/1000) / ${productInfo.rfcreep}) / ${productInfo.thickness})`;
        }

        // Convert to all time units as per DATABASE.txt
        const drainageTimeHours = drainageTimeSeconds / 3600; // DATABASE I column: =H/3600
        const drainageTimeDays = drainageTimeHours / 24; // DATABASE J column: =I/24
        const drainageTimeWeeks = drainageTimeDays / 7; // DATABASE K column: =J/7
        const drainageTimeMonths = drainageTimeDays / 30; // DATABASE L column: =J/30

        return {
            drainageTimeSeconds,
            drainageTimeHours,
            drainageTimeDays,
            drainageTimeWeeks,
            drainageTimeMonths,
            formula,
            productInfo,
        };
    }

    /**
     * Calculate enhanced drainage results with both short-term and long-term calculations
     * Integrates DATABASE.txt transmissivity formulas with existing drainage logic
     *
     * @param carriagewayWidth - Width of carriageway in meters
     * @param productId - MacDrain product ID
     * @param useTransmissivityFormula - Whether to use DATABASE.txt transmissivity formula
     * @returns Enhanced drainage calculation results
     */
    static calculateEnhancedDrainageResults(
        carriagewayWidth: number,
        productId: string,
        useTransmissivityFormula = true
    ): {
        shortTerm: {
            drainageTimeSeconds: number;
            drainageTimeHours: number;
            drainageTimeDays: number;
            drainageTimeWeeks: number;
            drainageTimeMonths: number;
            drainageQuality: string;
            formula: string;
        };
        longTerm: {
            drainageTimeSeconds: number;
            drainageTimeHours: number;
            drainageTimeDays: number;
            drainageTimeWeeks: number;
            drainageTimeMonths: number;
            drainageQuality: string;
            formula: string;
        };
        productInfo: DrainageProductProperties;
        calculationMethod: string;
    } {
        const productInfo = this.getDrainageProductProperties(productId);

        // Check if we can use transmissivity formula
        const canUseTransmissivity = useTransmissivityFormula && productInfo.transmissivity !== undefined && productInfo.thickness !== undefined;

        if (canUseTransmissivity) {
            // Use DATABASE.txt transmissivity formulas
            const shortTermResults = this.calculateTransmissivityDrainageTime(carriagewayWidth, productId, true);
            const longTermResults = this.calculateTransmissivityDrainageTime(carriagewayWidth, productId, false);

            // Determine drainage quality based on time thresholds
            const getDrainageQuality = (timeToDrainHours: number): string => {
                if (timeToDrainHours < 2) return 'Excellent';
                if (timeToDrainHours <= 24) return 'Good';
                if (timeToDrainHours <= 168) return 'Fair'; // 1 week
                if (timeToDrainHours <= 720) return 'Poor'; // 1 month
                return 'Very poor';
            };

            return {
                shortTerm: {
                    drainageTimeSeconds: shortTermResults.drainageTimeSeconds,
                    drainageTimeHours: shortTermResults.drainageTimeHours,
                    drainageTimeDays: shortTermResults.drainageTimeDays,
                    drainageTimeWeeks: shortTermResults.drainageTimeWeeks,
                    drainageTimeMonths: shortTermResults.drainageTimeMonths,
                    drainageQuality: getDrainageQuality(shortTermResults.drainageTimeHours),
                    formula: shortTermResults.formula,
                },
                longTerm: {
                    drainageTimeSeconds: longTermResults.drainageTimeSeconds,
                    drainageTimeHours: longTermResults.drainageTimeHours,
                    drainageTimeDays: longTermResults.drainageTimeDays,
                    drainageTimeWeeks: longTermResults.drainageTimeWeeks,
                    drainageTimeMonths: longTermResults.drainageTimeMonths,
                    drainageQuality: getDrainageQuality(longTermResults.drainageTimeHours),
                    formula: longTermResults.formula,
                },
                productInfo,
                calculationMethod: 'DATABASE.txt Transmissivity Formula',
            };
        } else {
            // Fallback to existing calculation method
            // For products without transmissivity data, use standard calculation
            const standardTime = 24; // Default fallback hours
            const getDrainageQuality = (timeToDrainHours: number): string => {
                if (timeToDrainHours < 2) return 'Excellent';
                if (timeToDrainHours <= 24) return 'Good';
                if (timeToDrainHours <= 168) return 'Fair';
                if (timeToDrainHours <= 720) return 'Poor';
                return 'Very poor';
            };

            return {
                shortTerm: {
                    drainageTimeSeconds: standardTime * 3600,
                    drainageTimeHours: standardTime,
                    drainageTimeDays: standardTime / 24,
                    drainageTimeWeeks: standardTime / 168,
                    drainageTimeMonths: standardTime / 720,
                    drainageQuality: getDrainageQuality(standardTime),
                    formula: 'Standard fallback calculation',
                },
                longTerm: {
                    drainageTimeSeconds: standardTime * 3600 * 1.2, // Assume 20% longer for long-term
                    drainageTimeHours: standardTime * 1.2,
                    drainageTimeDays: (standardTime * 1.2) / 24,
                    drainageTimeWeeks: (standardTime * 1.2) / 168,
                    drainageTimeMonths: (standardTime * 1.2) / 720,
                    drainageQuality: getDrainageQuality(standardTime * 1.2),
                    formula: 'Standard fallback calculation with creep factor',
                },
                productInfo,
                calculationMethod: 'Standard Fallback (no transmissivity data)',
            };
        }
    }

    /**
     * Calculate drainage coefficients using Excel's DRAINAGE_m logic with exact formula replication
     * CRITICAL FIX: Completely separated unstabilised and stabilised calculations to prevent cross-contamination
     *
     * @param carriagewayWidth - Width of carriageway in meters
     * @param surfaceSlope - Longitudinal slope (S)
     * @param crossSlope - Transversal slope (Sx)
     * @param baseThickness - Base layer thickness in meters
     * @param subbaseThickness - Subbase layer thickness in meters
     * @param baseEffectivePorosity - Base layer effective porosity (default: 0.32)
     * @param basePermeability - Base layer permeability in m/day (default: 8.46)
     * @param subbaseEffectivePorosity - Subbase layer effective porosity (default: 0.2)
     * @param subbasePermeability - Subbase layer permeability in m/day (default: 0.0846)
     * @param moistureExposure - Moisture exposure level
     * @param baseDrainageType - Base drainage product type
     * @param subbaseDrainageType - Subbase drainage product type
     */
    static calculateDrainageCoefficients(
        carriagewayWidth: number,
        surfaceSlope: number,
        crossSlope: number,
        baseThickness: number,
        subbaseThickness: number,
        baseEffectivePorosity: number,
        basePermeability: number,
        subbaseEffectivePorosity: number,
        subbasePermeability: number,
        moistureExposure: string,
        baseDrainageType?: string,
        subbaseDrainageType?: string
    ): any;

    /**
     * LEGACY OVERLOAD: Backward compatibility for existing code
     * When only layer-shared parameters are provided, uses Excel defaults:
     * - Base: porosity=0.32, permeability=8.46 m/day
     * - Subbase: porosity=0.2, permeability=0.0846 m/day
     */
    static calculateDrainageCoefficients(
        carriagewayWidth: number,
        surfaceSlope: number,
        crossSlope: number,
        baseThickness: number,
        subbaseThickness: number,
        sharedEffectivePorosity: number,
        sharedPermeability: number,
        moistureExposure: string,
        baseDrainageType?: string,
        subbaseDrainageType?: string
    ): any;

    // Implementation
    static calculateDrainageCoefficients(
        carriagewayWidth: number,
        surfaceSlope: number,
        crossSlope: number,
        baseThickness: number,
        subbaseThickness: number,
        arg6 = 0.32, // baseEffectivePorosity OR sharedEffectivePorosity
        arg7 = 8.46, // basePermeability OR sharedPermeability
        arg8?: number | string, // subbaseEffectivePorosity OR moistureExposure
        arg9?: number | string, // subbasePermeability OR baseDrainageType
        arg10?: string, // moistureExposure OR subbaseDrainageType
        arg11?: string, // baseDrainageType
        arg12?: string // subbaseDrainageType
    ) {
        // Determine signature and assign parameters correctly
        let baseEffectivePorosity: number;
        let basePermeability: number;
        let subbaseEffectivePorosity: number;
        let subbasePermeability: number;
        let moistureExposure: string;
        let baseDrainageType: string | undefined;
        let subbaseDrainageType: string | undefined;

        if (typeof arg8 === 'number') {
            // NEW SIGNATURE: 12 parameters with layer-specific properties
            baseEffectivePorosity = arg6;
            basePermeability = arg7;
            subbaseEffectivePorosity = arg8;
            subbasePermeability = arg9 as number;
            moistureExposure = arg10 || '5 - 25%';
            baseDrainageType = arg11;
            subbaseDrainageType = arg12;
        } else {
            // OLD SIGNATURE: 8-10 parameters with shared properties, use Excel defaults for each layer
            baseEffectivePorosity = 0.32; // Excel default for base
            basePermeability = 8.46; // Excel default for base
            subbaseEffectivePorosity = 0.2; // Excel default for subbase
            subbasePermeability = 0.0846; // Excel default for subbase
            moistureExposure = (arg8 as string) || '5 - 25%';
            baseDrainageType = arg9 as string;
            subbaseDrainageType = arg10;
        }

        // CRITICAL: Calculate unstabilised coefficients (ALWAYS standard road geometry, NO upgrades)
        const unstabilisedResults = this.calculateUnstabilisedCoefficients(
            carriagewayWidth,
            surfaceSlope,
            crossSlope,
            baseThickness,
            subbaseThickness,
            baseEffectivePorosity,
            basePermeability,
            subbaseEffectivePorosity,
            subbasePermeability,
            moistureExposure
        );

        // CRITICAL: Calculate stabilised coefficients (enhanced geometry ONLY if drainage products present)
        const stabilisedResults = this.calculateStabilisedCoefficients(
            carriagewayWidth,
            surfaceSlope,
            crossSlope,
            baseThickness,
            subbaseThickness,
            baseEffectivePorosity,
            basePermeability,
            subbaseEffectivePorosity,
            subbasePermeability,
            moistureExposure,
            baseDrainageType,
            subbaseDrainageType
        );

        // Use calculated coefficients directly - no mutual exclusivity workarounds needed
        // calculateStabilisedCoefficients() already handles table selection and the enhanced geometry
        // Note: Each layer (base/subbase) independently decides to use stabilised vs unstabilised coefficient

        // PAVED EXCEL LOGIC (Simplified - no conditional on base drainage):
        // G7 = Design_LCR!K12 (always table value)
        // G8 = Design_LCR!K25 (always table value)
        // H7 = IF(C4="No", G7, Design_LCR!K16) - checks base drainage
        // H8 = IF(C6="No", G8, Design_LCR!K29) - checks subbase drainage

        const finalResults = {
            // Base coefficients
            m2: unstabilisedResults.m2,
            m2_unstabilised: unstabilisedResults.m2, // G7: Always table value K12
            // H7 logic: Use STABILISED value when base has drainage (C4="Yes")
            m2_stabilised: baseDrainageType ? stabilisedResults.m2 : unstabilisedResults.m2,

            // Subbase coefficients
            m3: unstabilisedResults.m3,
            m3_unstabilised: unstabilisedResults.m3, // G8: Always table value K25
            // H8 logic: H8 = IF(C6="No", G8, K29)
            // When subbase has no drainage (C6="No") → copy G8 (unstabilised table value)
            // When subbase has drainage (C6="Yes") → use K29 (stabilised table value)
            m3_stabilised: subbaseDrainageType ? stabilisedResults.m3 : unstabilisedResults.m3,

            // Quality information
            // Use STABILISED quality when the layer has a drainage product (its enhanced geometry shortens the time to drain)
            // This matches the pattern for m2_stabilised and m3_stabilised above
            baseQuality: baseDrainageType ? stabilisedResults.baseQuality : unstabilisedResults.baseQuality,
            subbaseQuality: subbaseDrainageType ? stabilisedResults.subbaseQuality : unstabilisedResults.subbaseQuality,
            baseTimeToDrain: unstabilisedResults.baseTimeToDrain,
            subbaseTimeToDrain: unstabilisedResults.subbaseTimeToDrain,

            // Product enhancement info
            productInfo: {
                baseDrainageType,
                subbaseDrainageType,
                unstabilisedM2: unstabilisedResults.m2,
                stabilisedM2: stabilisedResults.m2,
                geometryUsed: stabilisedResults.geometryUsed,
            },
        };

        // ===============================================================================
        // DEBUG LOGGING - Final Drainage Coefficients Summary
        // ===============================================================================
        console.log('🎯 ========== FINAL DRAINAGE COEFFICIENTS ==========');
        console.log('📋 COEFFICIENTS SUMMARY:');
        console.log({
            m2_unstabilised: finalResults.m2_unstabilised,
            m2_stabilised: finalResults.m2_stabilised,
            m3_unstabilised: finalResults.m3_unstabilised,
            m3_stabilised: finalResults.m3_stabilised,
        });
        console.log('\n🔧 PRODUCT INFO:');
        console.log({
            baseDrainageType: finalResults.productInfo.baseDrainageType,
            subbaseDrainageType: finalResults.productInfo.subbaseDrainageType,
            hasBaseDrainage: baseDrainageType && this.isDrainageProduct(baseDrainageType),
            hasSubbaseDrainage: subbaseDrainageType && this.isDrainageProduct(subbaseDrainageType),
        });
        console.log('\n📊 QUALITY INFO:');
        console.log({
            baseQuality: finalResults.baseQuality,
            subbaseQuality: finalResults.subbaseQuality,
            baseTimeToDrain_hours: finalResults.baseTimeToDrain,
            subbaseTimeToDrain_hours: finalResults.subbaseTimeToDrain,
        });
        console.log('🎯 ==============================================\n');

        return finalResults;
    }

    /**
     * Calculate UNSTABILISED drainage coefficients
     * - ALWAYS uses standard road geometry
     * - NO product upgrades applied
     * - Uses UNSTABILISED VLOOKUP table (B82:F86)
     */
    private static calculateUnstabilisedCoefficients(
        carriagewayWidth: number,
        surfaceSlope: number,
        crossSlope: number,
        baseThickness: number,
        subbaseThickness: number,
        baseEffectivePorosity: number,
        basePermeability: number,
        subbaseEffectivePorosity: number,
        subbasePermeability: number,
        moistureExposure: string
    ) {
        // ALWAYS standard road geometry (no drainage enhancement)
        const safeCrossSlope = crossSlope === 0 ? 0.001 : crossSlope;
        const resultantSlope = Math.sqrt(surfaceSlope ** 2 + safeCrossSlope ** 2);
        const resultantLength = carriagewayWidth * Math.sqrt(1 + (surfaceSlope / safeCrossSlope) ** 2);

        return this.performDrainageCalculation(
            resultantSlope,
            resultantLength,
            baseThickness,
            subbaseThickness,
            baseEffectivePorosity,
            basePermeability,
            subbaseEffectivePorosity,
            subbasePermeability,
            moistureExposure,
            'UNSTABILISED',
            undefined, // NO product upgrades for unstabilised
            undefined
        );
    }

    /**
     * Calculate STABILISED drainage coefficients
     * - Uses layer-specific enhanced geometry when drainage is enabled for each layer
     * - Uses STABILISED VLOOKUP table (B91:F95)
     */
    private static calculateStabilisedCoefficients(
        carriagewayWidth: number,
        surfaceSlope: number,
        crossSlope: number,
        baseThickness: number,
        subbaseThickness: number,
        baseEffectivePorosity: number,
        basePermeability: number,
        subbaseEffectivePorosity: number,
        subbasePermeability: number,
        moistureExposure: string,
        baseDrainageType?: string,
        subbaseDrainageType?: string
    ) {
        // CRITICAL FIX: Layer-specific enhanced geometry
        // Excel logic: Each layer uses enhanced geometry ONLY if that specific layer has drainage enabled
        // - Base: IF drainage_below_base="Yes" (C4), base uses enhanced (D9=1, D10=thickness)
        // - Subbase: IF drainage_below_subbase="Yes" (C6), subbase uses enhanced (D34=1, D35=thickness)
        const hasBaseDrainageProduct = !!baseDrainageType && this.isDrainageProduct(baseDrainageType);
        const hasSubbaseDrainageProduct = !!subbaseDrainageType && this.isDrainageProduct(subbaseDrainageType);

        // Table selection: STABILISED if ANY drainage is enabled, matching Excel logic
        const hasDrainageEnabled = hasBaseDrainageProduct || hasSubbaseDrainageProduct;
        const tableType = hasDrainageEnabled ? 'STABILISED' : 'UNSTABILISED';

        // Standard road geometry (used as default, can be overridden per-layer)
        const safeCrossSlope = crossSlope === 0 ? 0.001 : crossSlope;
        const standardSlope = Math.sqrt(surfaceSlope ** 2 + safeCrossSlope ** 2);
        const standardLength = carriagewayWidth * Math.sqrt(1 + (surfaceSlope / safeCrossSlope) ** 2);

        const result = this.performDrainageCalculation(
            standardSlope,
            standardLength,
            baseThickness,
            subbaseThickness,
            baseEffectivePorosity,
            basePermeability,
            subbaseEffectivePorosity,
            subbasePermeability,
            moistureExposure,
            tableType,
            baseDrainageType,
            subbaseDrainageType,
            hasBaseDrainageProduct, // Pass layer-specific drainage info
            hasSubbaseDrainageProduct
        );

        const geometryUsed = `Base: ${hasBaseDrainageProduct ? 'enhanced' : 'standard'}, Subbase: ${hasSubbaseDrainageProduct ? 'enhanced' : 'standard'}`;

        return {
            ...result,
            geometryUsed,
        };
    }

    /**
     * Core drainage calculation logic shared by both calculations
     * CRITICAL: Supports layer-specific enhanced geometry for accurate Excel matching
     */
    private static performDrainageCalculation(
        resultantSlope: number,
        resultantLength: number,
        baseThickness: number,
        subbaseThickness: number,
        baseEffectivePorosity: number,
        basePermeability: number,
        subbaseEffectivePorosity: number,
        subbasePermeability: number,
        moistureExposure: string,
        tableType: 'UNSTABILISED' | 'STABILISED',
        baseDrainageType?: string,
        subbaseDrainageType?: string,
        hasBaseDrainage = false, // Layer-specific: base has drainage enabled
        hasSubbaseDrainage = false // Layer-specific: subbase has drainage enabled
    ) {
        // Get product properties for enhanced calculations
        const baseProductProps = this.getDrainageProductProperties(baseDrainageType);
        const subbaseProductProps = this.getDrainageProductProperties(subbaseDrainageType);

        // Use product-specific properties when drainage products are specified
        const finalBaseEffectivePorosity = baseDrainageType ? baseProductProps.effectivePorosity : baseEffectivePorosity;
        const finalBasePermeability = baseDrainageType ? baseProductProps.permeabilityCoefficient : basePermeability;
        const finalSubbaseEffectivePorosity = subbaseDrainageType ? subbaseProductProps.effectivePorosity : subbaseEffectivePorosity;
        const finalSubbasePermeability = subbaseDrainageType ? subbaseProductProps.permeabilityCoefficient : subbasePermeability;

        // CRITICAL FIX: Layer-specific enhanced geometry matching Excel formulas
        // Excel: IF(Drainage_Design!C4="Yes", 1, standard) for base slope
        // Excel: IF(Drainage_Design!C4="Yes", thickness, standard) for base length
        const baseResultantSlope = hasBaseDrainage ? 1.0 : resultantSlope;
        const baseResultantLength = hasBaseDrainage ? baseThickness : resultantLength;

        // Excel: IF(Drainage_Design!C6="Yes", 1, standard) for subbase slope
        // Excel: IF(Drainage_Design!C6="Yes", thickness, standard) for subbase length
        const subbaseResultantSlope = hasSubbaseDrainage ? 1.0 : resultantSlope;
        const subbaseResultantLength = hasSubbaseDrainage ? subbaseThickness : resultantLength;

        // Excel Formula D11: =D10*D9/D5
        // Slope factor: Sl = LR * SR / H (layer-specific)
        const baseSlopeFactor = (baseResultantLength * baseResultantSlope) / baseThickness;
        const subbaseSlopeFactor = (subbaseResultantLength * subbaseResultantSlope) / subbaseThickness;

        // Excel Formula D12: T50 calculation with exact Excel conditions
        const calculateT50 = (slopeFactor: number): number => {
            if (slopeFactor > 5.55) {
                return 0.7606 * Math.pow(slopeFactor, -1.35);
            } else if (slopeFactor > 1.32) {
                return 0.2438 * Math.pow(slopeFactor, -0.706);
            } else {
                return 0.2342 * Math.pow(slopeFactor, -0.444);
            }
        };

        const baseT50 = calculateT50(baseSlopeFactor);
        const subbaseT50 = calculateT50(subbaseSlopeFactor);

        // Excel Formula E15: m factor = No * LR² / (k * H) (layer-specific)
        const baseMFactor = (finalBaseEffectivePorosity * baseResultantLength ** 2) / (finalBasePermeability * baseThickness);
        const subbaseMFactor = (finalSubbaseEffectivePorosity * subbaseResultantLength ** 2) / (finalSubbasePermeability * subbaseThickness);

        // Excel Formula E16: Time to drain = T50 * m * 24
        const baseTimeToDrain = baseT50 * baseMFactor * 24;
        const subbaseTimeToDrain = subbaseT50 * subbaseMFactor * 24;

        // Excel VLOOKUP logic for drainage quality determination
        const getDrainageQuality = (timeToDrain: number): string => {
            if (timeToDrain < 2) return 'Excellent';
            if (timeToDrain <= 24) return 'Good';
            if (timeToDrain <= 168) return 'Fair';
            if (timeToDrain <= 720) return 'Poor';
            return 'Very poor';
        };

        // Excel DRAINAGE_m!C51/C26: quality comes only from the time to drain; the product
        // already shortens that time through the enhanced geometry above.
        const baseQuality = getDrainageQuality(baseTimeToDrain);
        const subbaseQuality = getDrainageQuality(subbaseTimeToDrain);

        // REMOVED INCORRECT MUTUAL EXCLUSIVITY LOGIC
        // Excel shows subbase coefficient remains constant when only base has drainage
        // Subbase quality should NOT be upgraded when base drainage is active
        // Only subbase's own drainage product should affect its quality

        // VLOOKUP coefficient mapping based on quality and moisture exposure
        const coefficientMap = this.getCoefficientsForTable(tableType);

        const m2 = this.lookupCoefficient(coefficientMap, baseQuality, moistureExposure);
        const m3 = this.lookupCoefficient(coefficientMap, subbaseQuality, moistureExposure);

        // ===============================================================================
        // DEBUG LOGGING - Drainage Coefficient Calculation
        // ===============================================================================
        console.log('💧 ========== DRAINAGE COEFFICIENT CALCULATION ==========');
        console.log('📊 INPUT PARAMETERS:');
        console.log({
            standardSlope: resultantSlope,
            standardLength: resultantLength,
            baseThickness,
            subbaseThickness,
            baseEffectivePorosity: finalBaseEffectivePorosity,
            basePermeability: finalBasePermeability,
            subbaseEffectivePorosity: finalSubbaseEffectivePorosity,
            subbasePermeability: finalSubbasePermeability,
            moistureExposure,
            tableType,
        });
        console.log('\n🔧 LAYER-SPECIFIC GEOMETRY:');
        console.log({
            baseHasDrainage: hasBaseDrainage,
            baseResultantSlope,
            baseResultantLength,
            subbaseHasDrainage: hasSubbaseDrainage,
            subbaseResultantSlope,
            subbaseResultantLength,
        });
        console.log('\n⏱️ TIME TO DRAIN CALCULATIONS:');
        console.log({
            baseSlopeFactor,
            baseT50,
            baseMFactor,
            baseTimeToDrain_hours: baseTimeToDrain,
            baseTimeToDrain_days: baseTimeToDrain / 24,
            subbaseSlopeFactor,
            subbaseT50,
            subbaseMFactor,
            subbaseTimeToDrain_hours: subbaseTimeToDrain,
            subbaseTimeToDrain_days: subbaseTimeToDrain / 24,
        });
        console.log('\n🎯 QUALITY DETERMINATION:');
        console.log({
            baseQuality,
            subbaseQuality,
            baseDrainageType,
            subbaseDrainageType,
        });
        console.log('\n📋 COEFFICIENT LOOKUP:');
        console.log({
            tableType,
            moistureExposure,
            m2_base: m2,
            m3_subbase: m3,
        });
        console.log('💧 ====================================================\n');

        return {
            m2,
            m3,
            baseQuality,
            subbaseQuality,
            baseTimeToDrain,
            subbaseTimeToDrain,
        };
    }

    /**
     * Get coefficient tables based on calculation type
     * REFACTORED: Now uses DrainageDataService (API-fetched) as Single Source of Truth
     * This fixes bugs: UNSTABILISED Good > 25% was 0.8 (now correctly 1.0 from Excel F83)
     * and wrong range '5 - 25%' (now correctly '2 - 25%' from Excel)
     */
    private static getCoefficientsForTable(tableType: 'UNSTABILISED' | 'STABILISED') {
        // Get from DrainageDataService (verified against Excel DRAINAGE_m.txt B82:F86 and B91:F95)
        const aashtoCoefficients = DrainageDataService.getAashtoCoefficients();
        return aashtoCoefficients[tableType] as any;
    }

    /**
     * VLOOKUP coefficient based on quality and moisture exposure
     * CORRECTED: Fallback now uses correct range '2 - 25%' (was wrong '5 - 25%')
     */
    private static lookupCoefficient(coefficientMap: any, quality: string, moistureExposure: string): number {
        const qualityRow = coefficientMap[quality];
        if (!qualityRow) {
            return coefficientMap['Fair']['2 - 25%'] || 1.05;
        }

        // Normalize moisture exposure to match table headers
        const normalizedExposure = this.normalizeMoistureExposure(moistureExposure);
        const coefficient = qualityRow[normalizedExposure];

        if (coefficient === undefined) {
            return qualityRow['2 - 25%'] || 1.05;
        }

        return coefficient;
    }

    /**
     * Normalize moisture exposure values to match Excel table headers
     * Excel HLOOKUP finds exact match between VLOOKUP result and table headers
     * VLOOKUP returns only these 4 exact strings, HLOOKUP searches for exact match
     * CORRECTED: Now uses correct range '2 - 25%' from Excel (was wrong '5 - 25%')
     */
    private static normalizeMoistureExposure(exposure: string): string {
        // Excel VLOOKUP returns only these 4 exact strings - no conversion needed
        const exposureMap: { [key: string]: string } = {
            '< 1%': '< 1%',
            '1 - 5%': '1 - 5%',
            '2 - 25%': '2 - 25%', // CORRECTED: Excel uses '2 - 25%' not '5 - 25%'
            '> 25%': '> 25%',
        };

        return exposureMap[exposure] || '2 - 25%';
    }
}
